/**
 * Material Requisitions Tab — unified site-to-store MR workflow.
 * Site raises MR → PM approves → Store issues or raises shortfall MPR.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, CheckCircle2, XCircle, Package, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiGet, apiPut, apiPost, apiGetAll } from '../../lib/api';
import { CreateMRForm } from '../../components/stores/CreateMRForm';
import { toast } from 'sonner';
import { formatDate } from '../../lib/format';

const STATUS_COLORS = {
  pending_pm_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_source_pm_approval: 'bg-orange-50 text-orange-700 border-orange-200',
  approved:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  transferred:         'bg-cyan-50 text-cyan-700 border-cyan-200',
  partial:             'bg-blue-50 text-blue-700 border-blue-200',
  issued:              'bg-slate-50 text-slate-600 border-slate-200',
  rejected:            'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_LABELS = {
  pending_pm_approval: 'Pending PM Approval',
  pending_source_pm_approval: 'Pending Source PM Approval',
  approved:            'Approved',
  transferred:         'Transferred',
  partial:             'Partially Issued',
  issued:              'Fully Issued',
  rejected:            'Rejected',
};

const SOURCE_LABELS = { own_site: 'Own Site', other_project: 'Another Project', godown: 'Central Godown' };

export function MRTab({ projectId }) {
  const [mrs, setMrs]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [availability, setAvailability] = useState({});
  const [loadingAvail, setLoadingAvail] = useState({});
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetAll('/stores/mr', projectId ? { project_id: projectId } : {});
      setMrs(r.items || []);
    } catch { toast.error('Failed to load material requisitions'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (mr) => {
    const id = mr.id;
    setExpanded(e => ({ ...e, [id]: !e[id] }));
    if (!expanded[id] && (mr.status === 'approved' || mr.status === 'transferred') && !availability[id]) {
      setLoadingAvail(l => ({ ...l, [id]: true }));
      try {
        const r = await apiGet(`/stores/mr/${id}/availability`);
        setAvailability(a => ({ ...a, [id]: r }));
      } catch { /* silent */ }
      finally { setLoadingAvail(l => ({ ...l, [id]: false })); }
    }
  };

  const approve = async (mr) => {
    try {
      const endpoint = mr.status === 'pending_source_pm_approval' ? 'source-pm-approve' : 'pm-approve';
      await apiPut(`/stores/mr/${mr.id}/${endpoint}`, { remarks: '' });
      toast.success(`MR ${mr.mr_no} approved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approval failed'); }
  };

  const reject = async (mr) => {
    const reason = window.prompt(`Reason for rejecting MR ${mr.mr_no}:`);
    if (!reason) return;
    try {
      await apiPut(`/stores/mr/${mr.id}/reject`, { reason });
      toast.success('MR rejected');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
  };

  const issueMR = async (mr) => {
    try {
      const r = await apiPost(`/stores/mr/${mr.id}/issue`, {});
      toast.success(`Issued — ${r.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Issue failed'); }
  };

  const raiseShortfallMPR = async (mr, avail) => {
    const shortItems = (avail?.items || [])
      .filter(it => it.shortfall_qty > 0)
      .map(it => ({ boq_id: it.boq_id, shortfall_qty: it.shortfall_qty }));
    if (!shortItems.length) { toast.info('No shortfall to raise MPR for'); return; }
    try {
      const r = await apiPost('/stores/mr/raise-shortfall-mpr', { mr_id: mr.id, items: shortItems });
      toast.success(`Shortfall MPR ${r.mpr_no} raised`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'MPR raise failed'); }
  };

  const filtered = mrs.filter(m =>
    !filter || (m.mr_no?.toLowerCase().includes(filter.toLowerCase()) ||
                m.project_name?.toLowerCase().includes(filter.toLowerCase()) ||
                m.requested_by_name?.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="mr-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display font-bold text-lg">Material Requisitions</h2>
        <div className="flex items-center gap-2">
          <input value={filter} onChange={e => setFilter(e.target.value)}
            placeholder="Search MR / project…"
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm w-52 focus:outline-none focus:border-[#3b4872]"
          />
          <button onClick={load} className="p-1.5 text-slate-500 hover:text-[#3b4872]">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowCreate(v => !v)} data-testid="mr-new-btn"
            className="flex items-center gap-1 bg-[#3b4872] hover:bg-[#2d3856] text-white text-sm font-semibold px-3 py-1.5 rounded-sm">
            <Plus size={14} /> New MR
          </button>
        </div>
      </div>
      {showCreate && (
        <CreateMRForm projectId={projectId} onCancel={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-slate-400">
          <Package size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="font-display font-bold text-slate-500 text-sm">No Material Requisitions</p>
          <p className="text-xs mt-1">Use <b>New MR</b> above to raise a requisition against the project's Material Sheet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(mr => {
            const isOpen = expanded[mr.id];
            const avail = availability[mr.id];
            const statusCls = STATUS_COLORS[mr.status] || 'bg-slate-50 text-slate-600 border-slate-200';
            return (
              <div key={mr.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`mr-row-${mr.id}`}>
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleExpand(mr)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm flex items-center gap-1.5">
                        {mr.mr_no}
                        {mr.source_type && mr.source_type !== 'own_site' && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`mr-source-${mr.id}`}>
                            from {mr.source_type === 'godown' ? 'Central Godown' : mr.source_project_name || SOURCE_LABELS[mr.source_type]}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {mr.project_name} · {mr.requested_by_name} · {(mr.items || []).slice(0, 2).map(it =>
                          `${it.description || it.item_code || it.purpose || it.boq_id || '—'} — ${it.requested_qty ?? ''}${it.unit ? ` ${it.unit}` : ''}${it.required_by_date ? ` (by ${formatDate(it.required_by_date)})` : ''}`
                        ).join(', ')}{(mr.items || []).length > 2 ? ` +${(mr.items || []).length - 2} more` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusCls}`}>
                      {STATUS_LABELS[mr.status] || mr.status}
                    </span>
                    {(mr.status === 'pending_pm_approval' || mr.status === 'pending_source_pm_approval') && (
                      <>
                        <button onClick={e => { e.stopPropagation(); approve(mr); }}
                          className="text-xs font-bold text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                          data-testid={`mr-approve-${mr.id}`}>
                          <CheckCircle2 size={13} /> Approve
                        </button>
                        <button onClick={e => { e.stopPropagation(); reject(mr); }}
                          className="text-xs font-bold text-rose-600 hover:text-rose-800 flex items-center gap-1"
                          data-testid={`mr-reject-${mr.id}`}>
                          <XCircle size={13} /> Reject
                        </button>
                      </>
                    )}
                    {(mr.status === 'approved' || mr.status === 'partial' || mr.status === 'transferred') &&
                      !(['other_project', 'godown'].includes(mr.source_type) && mr.status === 'approved') && (
                      <button onClick={e => { e.stopPropagation(); issueMR(mr); }}
                        className="text-xs font-bold text-[#3b4872] border border-[#3b4872] rounded px-2 py-0.5 hover:bg-[#3b4872]/5"
                        data-testid={`mr-issue-${mr.id}`}>
                        Issue
                      </button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                    {loadingAvail[mr.id] ? (
                      <p className="text-xs text-slate-400">Checking stock…</p>
                    ) : avail ? (
                      <>
                        {avail.has_shortfall && (
                          <div className="flex items-center justify-between mb-2 p-2 bg-amber-50 border border-amber-200 rounded-sm">
                            <span className="text-xs text-amber-700 flex items-center gap-1.5">
                              <AlertTriangle size={12} /> Stock shortfall detected
                            </span>
                            <button onClick={() => raiseShortfallMPR(mr, avail)}
                              className="text-[10px] font-bold text-amber-700 border border-amber-300 rounded px-2 py-0.5 hover:bg-amber-100"
                              data-testid={`mr-raise-mpr-${mr.id}`}>
                              Raise Shortfall MPR
                            </button>
                          </div>
                        )}
                        <table className="w-full text-xs">
                          <thead className="text-slate-500 border-b border-slate-100">
                            <tr>
                              <th className="text-left py-1 pr-3">Item</th>
                              <th className="text-right pr-3">Required</th>
                              <th className="text-right pr-3">Available</th>
                              <th className="text-right text-rose-600">Shortfall</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {avail.items.map((it, i) => (
                              <tr key={i}>
                                <td className="py-1 pr-3 font-medium">{it.description || it.boq_id}</td>
                                <td className="text-right pr-3">{it.outstanding_qty} {it.unit}</td>
                                <td className="text-right pr-3">{it.available_qty} {it.unit}</td>
                                <td className={`text-right font-bold ${it.shortfall_qty > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {it.shortfall_qty > 0 ? `-${it.shortfall_qty}` : '✓'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-slate-500 border-b border-slate-100">
                          <tr>
                            <th className="text-left py-1 pr-3">Material</th>
                            <th className="text-right pr-3">Requested</th>
                            <th className="text-right pr-3">Issued</th>
                            <th className="text-left pr-3">Required By</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {(mr.items || []).map((it, i) => (
                            <tr key={i} data-testid={`mr-line-${mr.id}-${i}`}>
                              <td className="py-1 pr-3">{it.description || it.item_code || it.purpose || it.boq_id}{it.unit ? ` (${it.unit})` : ''}</td>
                              <td className="text-right pr-3">{it.requested_qty}</td>
                              <td className="text-right pr-3">{it.issued_qty || 0}</td>
                              <td className="text-left text-slate-500">{it.required_by_date ? formatDate(it.required_by_date) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {mr.remarks && <p className="text-xs text-slate-500 mt-2">Remarks: {mr.remarks}</p>}
                    {mr.rejection_reason && <p className="text-xs text-rose-600 mt-2">Rejected: {mr.rejection_reason}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
